import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AppContext } from '../../contexts/app-context';
import {
  useReportRouteLoading,
  useRetryRoute,
} from '../../hooks/use-route-loading';
import { AllianceFooter } from '../alliance/alliance-footer';
import { LoadFailedScreen } from '../alliance/system-screens';
import { AnimateInProps } from '../animate-in';
import { RouteErrorBoundary } from './route-error-boundary';
import { RouteWrapper } from './route-wrapper';

/**
 * Wraps every top-level page: the document title, the header content, then
 * the content (in an error boundary) with the Alliance footer (unless
 * `hideFooter`).
 *
 * While `loading`, the header stays and the content area is empty; the shell
 * shows the 3px bar under the App Bar after 150ms. `hasError` shows
 * "Couldn't load this page" (offline vs. server trouble) with Try again,
 * which refetches failed queries and remounts the page.
 */
export function RouteContainer(props: {
  children: React.ReactNode;
  routeTitle: string;
  loading?: boolean;
  headerContent?: React.ReactNode;
  hasError?: boolean;
  paddingBottom?: number;
  hideHeader?: boolean;
  hideFooter?: boolean;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
  noanimation?: boolean;
}) {
  // Context
  const { dispatch } = React.useContext(AppContext);
  // Hooks
  const retryRoute = useRetryRoute();
  useReportRouteLoading({ loading: !!props.loading });

  React.useEffect(() => {
    // If the drawer is open when this component mounts, close it
    dispatch({ type: 'DRAWER_CLOSE' });
  }, [dispatch]);

  let routeContent: JSX.Element | null = null;
  if (props.loading) {
    routeContent = null;
  } else if (props.hasError) {
    routeContent = <LoadFailedScreen layout="inline" onRetry={retryRoute} />;
  } else {
    routeContent = (
      <RouteErrorBoundary>
        <RouteWrapper
          paddingBottom={props.paddingBottom}
          nofade={props.nofade}
          notransform={props.notransform}
          noanimation={props.noanimation}
          footer={props.hideFooter ? undefined : <AllianceFooter />}
        >
          {props.children}
        </RouteWrapper>
      </RouteErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>NAAU · {props.routeTitle}</title>
      </Helmet>

      {props.headerContent}

      <div role="main">{routeContent}</div>
    </React.Fragment>
  );
}
